import { inject, Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';

import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LoaderService } from '../../services/loader/loader.service';
import {
  ErrorMessagePathsToIgnore,
  ErrorMessagesToIgnore,
} from '../../models/error-messages-to-ignore';
import {
  AnalyticsEvents,
  AnalyticsService,
} from 'src/app/analytics/services/analytics/analytics.service';

@Injectable()
export class ErrorCatchingInterceptor implements HttpInterceptor {
  private readonly loaderService = inject(LoaderService);
  private readonly analyticsService = inject(AnalyticsService);

  errorMessage?: string;
  path: string | undefined;

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        let errorMsg = '';
        if (error.error instanceof ErrorEvent) {
          errorMsg = `${error.error.message}`;
          return throwError(() => new Error(errorMsg));
        } else {
          return throwError(() => new Error(error.message));
        }
      }),
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const response = event.body;
          const errorExists = response?.errors;
          if (errorExists && errorExists?.length > 0) {
            const firstError = errorExists[0];
            const correlationId = `${firstError.extensions?.['correlationId']}`;
            this.path =
              this.errorPathForAnalytics(
                firstError.extensions?.['path'] ?? firstError.path
              ) ?? undefined;
            if (
              firstError?.extensions?.['errorDetail'] &&
              firstError?.extensions?.['correlationId']
            ) {
              this.errorMessage = `<b>Message:</b> ${firstError.extensions['errorDetail']} </br> <b>CorrelationId:</b> ${firstError.extensions['correlationId']}`;
            } else if (
              firstError?.extensions?.['errorDetail'] &&
              !firstError?.extensions?.['correlationId']
            ) {
              this.errorMessage = `<b>Message:</b> ${firstError.extensions['errorDetail']}`;
            } else {
              this.errorMessage = `<b>Message:</b> ${firstError['message']}`;
            }

            this.trackGraphqlBodyErrorAnalytics(firstError);

            // Only throw loader service error if error message is not one we expect
            // and want to handle differently.
            // NOTE: Once the subscription for get loan counts by status is done,
            // this hasIgnoredPath check can be removed.
            const hasIgnoredPath = Object.values(
              ErrorMessagePathsToIgnore
            ).some((path) => this.path?.includes(path));
            const hasIgnoredMessage =
              Object.keys(ErrorMessagesToIgnore).filter((message) =>
                this.errorMessage?.includes(message)
              ).length > 0;

            const shouldError = !hasIgnoredPath && !hasIgnoredMessage;

            if (shouldError) {
              this.loaderService.error(
                this.path ?? '',
                this.errorMessage,
                correlationId
              );
            }
          }
        }
        return event;
      })
    );
  }

  private trackGraphqlBodyErrorAnalytics(firstError: {
    extensions?: Record<string, unknown>;
    message?: string;
    path?: string | readonly string[];
  }): void {
    const ext = firstError.extensions;
    const rawCorrelation = ext?.['correlationId'];
    const correlation_id =
      rawCorrelation != null && String(rawCorrelation).trim() !== ''
        ? String(rawCorrelation)
        : undefined;

    this.analyticsService.trackEvent(AnalyticsEvents.API_ERROR, {
      feature: 'error_catching_interceptor',
      error_path: this.errorPathForAnalytics(this.path),
      correlation_id,
      error_detail: this.errorDetailForAnalytics(firstError),
    });
  }

  private errorDetailForAnalytics(firstError: {
    extensions?: Record<string, unknown>;
    message?: string;
  }): string | undefined {
    const extDetail = firstError.extensions?.['errorDetail'];
    if (extDetail != null && String(extDetail).trim() !== '') {
      return String(extDetail);
    }
    const msg = firstError.message;
    if (msg != null && String(msg).trim() !== '') {
      return String(msg);
    }
    return undefined;
  }

  private errorPathForAnalytics(
    path: string | string[] | undefined
  ): string | undefined {
    if (path == null) {
      return undefined;
    }
    return Array.isArray(path) ? path.join('.') : path;
  }
}
